import { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { Checkbox } from 'ui-components/components/checkbox';

import { AutomationRulesContext } from '../../../../context';
import { ACTION_TYPE_SET_SHIP_DATE } from '../../../../constants/action-types';
import { DateTypes, SKIP_REPROCESS_SUFFIX } from './action-value-input/set-ship-date/set-ship-date';
import { ActionSelector } from './action-selector';
import ActionRemover from './action-remover';
import { actionValueInputFromActionType, actionTypeIdHasMatchingComponent } from '../helpers/action-value-input-from-action-type';
import { componentLabelFromActionType } from '../helpers/component-label-from-action-type';
import { PARAMETER, INT_PARAMETER } from '../../../../constants/parameter-name-for-update';
import { messages } from './action-value-input/set-ship-date/messages';
import NoInputComponent from './no-input-component';
import style from '../style.module.less';

const RuleActionEditor = ({ orderRuleActionId }) => {
  const { selectedRule, setSelectedRule, orderRuleActionIdsWithError } = useContext(AutomationRulesContext);
  const { orderRuleActions } = selectedRule;
  const action = orderRuleActions.find((rule) => rule && rule.orderRuleActionId === orderRuleActionId);
  const indexOfAction = orderRuleActions.indexOf(action);
  const InputType = actionValueInputFromActionType(action?.actionTypeId);

  const isActionInputRequired = actionTypeIdHasMatchingComponent(action?.actionTypeId) && InputType !== NoInputComponent;
  const isActionValueEmpty = action?.parameter == null && action?.intParameter == null;
  const isUnpopulated = selectedRule.isTemplate && isActionInputRequired && isActionValueEmpty;

  const onActionTypeChange = useCallback(
    (actionTypeId) => {
      const replacementAction = {
        ...action,
        actionTypeId,
        [INT_PARAMETER]: undefined,
        [PARAMETER]: undefined,
      };
      const newActions = [...orderRuleActions];
      newActions.splice(indexOfAction, 1, replacementAction);
      setSelectedRule({
        ...selectedRule,
        orderRuleActions: [...newActions],
      });
    },
    [action, selectedRule, setSelectedRule, indexOfAction, orderRuleActions],
  );

  const onActionValueChange = useCallback(
    (newProps) => {
      const replacementAction = {
        ...action,
        ...newProps,
      };
      const newActions = [...orderRuleActions];
      newActions.splice(indexOfAction, 1, replacementAction);
      setSelectedRule({
        ...selectedRule,
        orderRuleActions: [...newActions],
      });
    },
    [action, selectedRule, setSelectedRule, indexOfAction, orderRuleActions],
  );

  const inputValueProps = {
    [INT_PARAMETER]: action.intParameter,
    [PARAMETER]: action.parameter,
  };
  const hasError = orderRuleActionIdsWithError.includes(orderRuleActionId);

  const isSetShipDateAction = action?.actionTypeId === ACTION_TYPE_SET_SHIP_DATE;
  const isShipDateWithCurrentDate = isSetShipDateAction && action?.parameter?.includes(DateTypes.CurrentDate);
  const skipReprocess = isSetShipDateAction && action?.parameter?.includes(SKIP_REPROCESS_SUFFIX);

  const onChangeSkipReprocess = useCallback(
    (checked) => {
      const baseParameter = action?.parameter?.replace(SKIP_REPROCESS_SUFFIX, '') || DateTypes.CurrentDate;
      onActionValueChange({
        [PARAMETER]: checked ? `${baseParameter}${SKIP_REPROCESS_SUFFIX}` : baseParameter,
      });
    },
    [action?.parameter, onActionValueChange],
  );

  // @design-input-pending
  return (
    <div className={style['action-editor']}>
      <div className={style['action-properties']}>
        <ActionSelector onActionTypeChange={onActionTypeChange} selectedActionTypeId={action?.actionTypeId} />
        <InputType
          actionTypeId={action?.actionTypeId}
          className={classnames({
            [style['highlighted-template-input']]: isUnpopulated,
          })}
          hasError={hasError}
          inputValueProps={inputValueProps}
          label={componentLabelFromActionType(action?.actionTypeId)}
          onChange={onActionValueChange}
        />
      </div>
      <ActionRemover orderRuleActionId={action?.orderRuleActionId} />
      {isShipDateWithCurrentDate && (
        <Checkbox checked={skipReprocess} className={style['skip-reprocess-checkbox']} onChange={onChangeSkipReprocess}>
          <FormattedMessage {...messages.skipReprocessing} />
        </Checkbox>
      )}
    </div>
  );
};

RuleActionEditor.propTypes = {
  orderRuleActionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default RuleActionEditor;
